import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Mail, Lock, AlertCircle, Sun, Moon, Eye, EyeOff } from 'lucide-react'
import { useSignature } from '../contexts/SignatureContext'
import { getGalateaLogoUrl } from '../utils/tenantLogo'

const createLoginSchema = (t: (key: string) => string) => z.object({
  email: z.string().min(1, 'Email ou login ThorJava é obrigatório'),
  password: z.string().min(1, t('auth.password') + ' obrigatória'),
})

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>

const LoginPage: React.FC = () => {
  const { t } = useTranslation()
  const loginSchema = React.useMemo(() => createLoginSchema(t), [t])
  const [error, setError] = useState<string | null>(null)
  const [sessionMessage, setSessionMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { login } = useAuth()
  const { mode, toggleMode } = useSignature()
  const navigate = useNavigate()

  useEffect(() => {
    const storedMessage = sessionStorage.getItem('session_expired_message')
    if (storedMessage) {
      setSessionMessage(storedMessage)
      sessionStorage.removeItem('session_expired_message')
      sessionStorage.removeItem('session_expired_timestamp')
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    console.log('[LoginPage] 🔐 Iniciando login...')
    setError(null)
    setIsLoading(true)
    sessionStorage.removeItem('tokens_cleared')

    try {
      console.log('[LoginPage] [1/4] Chamando login()...')
      await login(data)
      console.log('[LoginPage] [2/4] Login concluído, redirecionando para seleção de contexto...')
      
      // FORÇAR sempre ir para seleção de contexto para evitar loops e garantir carregamento correto
      // O ProtectedRoute vai cuidar de redirecionar se já tiver contexto, mas aqui garantimos o fluxo limpo
      navigate('/context-selection', { replace: true })
      
    } catch (err: any) {
      console.error('[LoginPage] ❌ Erro no login:', err)
      setError(err.response?.data?.detail || t('auth.invalidCredentials'))
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#eef4fa] text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(15,91,215,0.10),_transparent_34%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.10),_transparent_30%)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.12),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(37,99,235,0.16),_transparent_28%)]" />
      <div className="absolute right-6 top-6 z-20">
        <button onClick={toggleMode} type="button" aria-label={mode === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm backdrop-blur hover:bg-white dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-300 dark:hover:bg-slate-900">
          {mode === 'dark' ? <Sun size={15} className="text-amber-400" /> : <Moon size={15} className="text-[#0f5bd7]" />}
          {mode === 'dark' ? 'Modo Claro' : 'Modo Escuro'}
        </button>
      </div>
      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid w-full items-center gap-10 lg:grid-cols-[1.05fr_480px]">
          <section className="hidden lg:block space-y-6 pr-6">
            <img src={getGalateaLogoUrl()} alt="Galatea" className="h-16 w-auto object-contain" />
            <div className="space-y-4">
              <p className="max-w-xl text-5xl font-semibold tracking-tight text-slate-900 dark:text-white">Sua empresa conectada em uma única plataforma.</p>
              <p className="max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">Vendas, estoque, financeiro, fiscal e inteligência para apoiar suas decisões com fluidez, segurança e contexto.</p>
            </div>
          </section>
          <div className="space-y-6">
            <div className="rounded-[32px] border border-slate-200/80 bg-white/92 p-8 shadow-[0_24px_70px_rgba(15,23,42,0.10)] backdrop-blur sm:p-10 dark:border-slate-800 dark:bg-slate-900/92 dark:shadow-[0_24px_70px_rgba(2,6,23,0.45)]">
              <div className="space-y-6">
                <div className="space-y-4 text-center">
                  <div className="flex justify-center"><img src={getGalateaLogoUrl()} alt="Galatea" className="h-18 w-auto object-contain" /></div>
                  <div className="space-y-2"><h1 className="text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Bem-vindo ao Galatea</h1><p className="text-base text-slate-600 dark:text-slate-300">Gestão empresarial integrada e inteligente</p></div>
                </div>
                {sessionMessage && <div className="flex items-start gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"><AlertCircle size={18} className="mt-0.5 shrink-0" /><span>{sessionMessage}</span></div>}
                {error && <div className="flex items-start gap-2.5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"><AlertCircle size={18} className="mt-0.5 shrink-0" /><span>{error}</span></div>}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <Input label="E-mail ou usuário" placeholder="usuario@empresa.com" size="md" {...register('email')} error={errors.email?.message} leftIcon={<Mail size={18} className="text-slate-400" />} className="h-14 rounded-2xl border-slate-200 bg-slate-50/80 text-slate-900 placeholder-slate-400 focus:border-[#0f5bd7] focus:ring-[#0f5bd7]/15 dark:border-slate-700 dark:bg-slate-950/60 dark:text-white" />
                  <div className="space-y-2"><div className="flex items-center justify-between gap-3"><label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300">Senha</label><Link to="/forgot-password" className="text-xs font-semibold text-[#0f5bd7] hover:underline dark:text-sky-300">Esqueci minha senha</Link></div><div className="relative"><div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400"><Lock size={18} /></div><input id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" {...register('password')} className="h-14 w-full rounded-2xl border border-slate-200 bg-slate-50/80 pl-11 pr-12 text-sm text-slate-900 placeholder-slate-400 focus:border-[#0f5bd7] focus:outline-none focus:ring-4 focus:ring-[#0f5bd7]/15 dark:border-slate-700 dark:bg-slate-950/60 dark:text-white" /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'} className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>{errors.password?.message && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{errors.password?.message}</p>}</div>
                  <Button type="submit" className="mt-2 h-14 w-full rounded-2xl bg-[#0f5bd7] text-base font-semibold text-white shadow-[0_16px_36px_rgba(15,91,215,0.25)] hover:bg-[#0c4fbc]" isLoading={isLoading}>Entrar no Galatea</Button>
                </form>
              </div>
            </div>
            <p className="px-2 text-center text-xs text-slate-600 dark:text-slate-400">&copy; {new Date().getFullYear()} Galatea | Política de Privacidade | Termos de Uso</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
